import "intersection-observer";

/**
 * If IntersectionObserver and WeakMap are implemented, if
 * the image is not inside viewport, it renders an empty div helping to reduce
 * bandwidth usage.
 *
 * By default, it renders the img element or a div element with children and
 * backgroundImage CSS property set
 */
import React from "react";

// See https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
const placeholderObserver =
  typeof IntersectionObserver === "function"
    ? new IntersectionObserver(onIntersectionChanged, {
        rootMargin: "0% 0% 50% 0%",
      })
    : null;

const placeholdersToCallbacks =
  typeof WeakMap === "function" ? new WeakMap() : null;

const canLoadLazy = !!placeholderObserver && !!placeholdersToCallbacks;

function onIntersectionChanged(entries: IntersectionObserverEntry[]) {
  placeholdersToCallbacks &&
    entries.forEach(e => {
      const handler = placeholdersToCallbacks.get(e.target);
      handler && handler(e);
    });
}

interface LazyImageProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onLoad"> {
  src?: string;
  alt?: string;
  style?: React.CSSProperties;
  renderAsBackground?: boolean;
  children?: React.ReactNode;
  onLoad?: () => void;
}

interface LazyImageState {
  showImage?: boolean;
  shouldRunOnLoad?: boolean;
}

// TODO: Use React.forwardRef
export default class LazyImage extends React.Component<
  LazyImageProps,
  LazyImageState
> {
  constructor(props: LazyImageProps) {
    super(props);
    this.state = {};
  }
  componentDidMount() {
    if (canLoadLazy) {
      this.observePlaceholder();
    }
  }

  componentWillUnmount() {
    if (canLoadLazy) {
      this.unobservePlaceholder();
    }
  }

  handleObserver = (entry: IntersectionObserverEntry) => {
    if (entry.isIntersecting) {
      this.unobservePlaceholder();
      this.setState({ showImage: true });
    }
  };

  observePlaceholder = () => {
    if (this.refs.placeholder) {
      placeholdersToCallbacks?.set(this.refs.placeholder, this.handleObserver);
      placeholderObserver?.observe(this.refs.placeholder as Element);
    }
  };

  unobservePlaceholder = () => {
    if (this.refs.placeholder) {
      placeholderObserver?.unobserve(this.refs.placeholder as Element);
      placeholdersToCallbacks?.delete(this.refs.placeholder);
    }
  };

  render() {
    const {
      renderAsBackground,
      children,
      src,
      style = {},
      alt = "",
      onLoad,
      ...other
    } = this.props;
    const { showImage } = this.state;

    if (
      this.state.showImage === true &&
      this.state.shouldRunOnLoad !== true &&
      typeof onLoad === "function"
    ) {
      onLoad();
      this.setState({
        shouldRunOnLoad: true,
      });
    }

    if (canLoadLazy && !showImage) {
      const {
        backgroundImage: _backgroundImage,
        ...styleWithoutBackgroundImage
      } = style;
      return (
        <div ref="placeholder" {...other} style={styleWithoutBackgroundImage}>
          {children}
        </div>
      );
    }
    return renderAsBackground ? (
      <div {...other} style={style}>
        {!!children && children}
      </div>
    ) : (
      <img alt={alt} {...other} src={src} style={style} />
    );
  }
}
